// Phase SIKA-5e/2/3 — Claude.ai-style composer.
//
// Floating bottom card with: attach (+) button, auto-grow textarea,
// optional voice mic, send button, tier pill, free-tier soft cap.
//
// Voice plumbing kept verbatim from the legacy SikaChatInput so the
// behaviour is identical — Web Speech API, interim+final transcripts,
// onend/onerror reset. The wire-up will be retired together with
// SikaChatInput in 5e/5.
//
// Chrome is the new .sc2-composer-* classes from super-acca.css.

import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { Icon } from "../../../components/icons/Icon.tsx";

const SPEECH_AVAILABLE =
  typeof window !== "undefined" &&
  ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

const FREE_WEEKLY_CAP = 5;

export interface SikaComposerProps {
  /** Called with the trimmed message text on submit. */
  onSend: (text: string) => void;
  /** True while a response is in-flight — disables send + textarea. */
  disabled: boolean;
  /** Pro tier flag → controls the tier pill + soft cap visibility. */
  pro?: boolean;
  /** Optional slot before the textarea (e.g. + button → GoalPopover). */
  leftSlot?: ReactNode;
  /** User-message count within the rolling 7-day window. When pro=false
   * AND > 0, a small "N/5 this week" pill renders next to the tier. */
  weeklyUserMessageCount?: number;
  /** Placeholder copy. Default: "Message SIKA". */
  placeholder?: string;
}

export function SikaComposer({
  onSend,
  disabled,
  pro = false,
  leftSlot = null,
  weeklyUserMessageCount = 0,
  placeholder = "Message SIKA",
}: SikaComposerProps): JSX.Element {
  const [value, setValue] = useState("");
  const [listening, setListening] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<unknown>(null);

  const hasText = value.trim().length > 0;
  const showCapPill = !pro && weeklyUserMessageCount > 0;

  const toggleVoice = useCallback(() => {
    if (!SPEECH_AVAILABLE) return;
    if (listening) {
      const rec = recognitionRef.current as { stop: () => void } | null;
      rec?.stop();
      setListening(false);
      return;
    }
    const SpeechRecognition =
      (window as unknown as Record<string, unknown>).SpeechRecognition ??
      (window as unknown as Record<string, unknown>).webkitSpeechRecognition;
    const recognition = new (SpeechRecognition as new () => {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      onresult: (e: {
        results: { transcript: string; isFinal: boolean }[][];
      }) => void;
      onend: () => void;
      onerror: () => void;
      start: () => void;
      stop: () => void;
    })();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-GH";
    recognition.onresult = (e) => {
      const last = e.results[e.results.length - 1];
      if (last?.[0]) {
        setValue((prev) => {
          const base = prev.replace(/\[listening\.\.\.\]$/, "").trim();
          const transcript = last[0]!.transcript;
          return base ? `${base} ${transcript}` : transcript;
        });
      }
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [listening]);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, disabled, onSend]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const onInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${String(Math.min(el.scrollHeight, 200))}px`;
  }, []);

  return (
    <div className="sc2-composer-wrap" data-testid="sc2-composer-wrap">
      <div className="sc2-composer" data-testid="sc2-composer">
        <div className="sc2-composer-row">
          {leftSlot ? <div data-testid="sc2-composer-left">{leftSlot}</div> : null}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            onInput={onInput}
            placeholder={placeholder}
            aria-label="Message SIKA"
            rows={1}
            disabled={disabled}
            data-testid="sc2-composer-textarea"
          />
          {SPEECH_AVAILABLE ? (
            <button
              type="button"
              className="sc2-icon-btn"
              onClick={toggleVoice}
              disabled={disabled}
              aria-label={listening ? "Stop voice input" : "Voice input"}
              data-testid="sc2-composer-voice"
              data-listening={listening || undefined}
            >
              <Icon name={listening ? "close" : "mic"} size={16} />
            </button>
          ) : null}
          <button
            type="button"
            className="sc2-send-btn"
            onClick={submit}
            disabled={disabled || !hasText}
            aria-label="Send message"
            data-testid="sc2-composer-send"
          >
            <Icon name="arrow" size={16} />
          </button>
        </div>
        <div className="sc2-composer-meta">
          <span
            className="sc2-tier-pill"
            data-tier={pro ? "pro" : "free"}
            data-testid="sc2-tier-pill"
          >
            {pro ? "SIKA Pro" : "SIKA"}
          </span>
          {showCapPill ? (
            <span
              className="sc2-tier-pill"
              data-testid="sc2-cap-pill"
              title="Free tier — 5 messages per week (advisory)"
            >
              {`${weeklyUserMessageCount}/${FREE_WEEKLY_CAP} this week`}
            </span>
          ) : null}
          <span style={{ marginLeft: "auto" }}>
            Enter to send · Shift+Enter for line
          </span>
        </div>
      </div>
      <div className="sc2-disclaimer">SIKA is AI and can make mistakes.</div>
    </div>
  );
}
